import React from "react";

import {
  iconVariants,
  rounds,
  sizes,
  variants,
} from "@/components/ui/Badge/utils";
import { IconsProps } from "@/components/ui/Button";

import cn from "@/utils/cn";

export type Props = React.HTMLAttributes<HTMLSpanElement> & {
  size?: keyof typeof sizes;
  variant?: keyof typeof variants;
  round?: keyof typeof rounds;
  children: React.ReactNode;
  fontSize?: string;
  fontWeight?: string;
  fontFamily?: string;
} & IconsProps;

export const Badge = React.forwardRef<HTMLSpanElement, Props>(
  (
    {
      size = "sm",
      variant = "green",
      startIcon,
      endIcon,
      className = "",
      children,
      round = "xl",
      fontSize,
      fontWeight,
      fontFamily,
      ...props
    },
    ref,
  ) => {
    return (
      <span
        ref={ref}
        className={cn(
          "group relative inline-flex max-w-[150px] items-center mix-blend-multiply",
          sizes[size],
          variants[variant],
          rounds[round],
          className,
          fontSize ? `text-[${fontSize}]` : "",
          fontFamily ? `font-${fontFamily}` : "",
          `font-${fontWeight ? fontWeight : "semibold"}`,
        )}
        data-testid={`badge-${variant}`}
        {...props}
      >
        {startIcon && (
          <span>
            {React.cloneElement(startIcon, {
              className: iconVariants[variant],
              "data-testid": "badge-start-icon",
            })}
          </span>
        )}
        <span
          className={cn("truncate", { "pl-1": startIcon, "pr-1": endIcon })}
        >
          {children}
        </span>
        {endIcon && (
          <span>
            {React.cloneElement(endIcon, {
              className: iconVariants[variant],
              "data-testid": "badge-end-icon",
            })}
          </span>
        )}
      </span>
    );
  },
);
Badge.displayName = "Badge";
